/**
 * SignalAudio — 各天体の「シグナル音」を WebAudio オシレーターで生成する。
 *
 * 音声ファイルではなくオシレーターを使う理由:
 * - sources.js のロードゲート（実在しないファイルで ready が発火しなくなる）にリスクゼロ
 * - 照準の合い具合に応じた連続的な音量制御は、常駐 GainNode が最適
 * - 天体ごとに異なる4種の音色を数十行で作れる
 *
 * 出力は AudioMixer の SE グループへ接続するので、ミキサーの音量設定に従う。
 * setLevel(name, 0..1) の API を保っているので、後で音声ファイルのループ再生に
 * 差し替える場合もこのファイルの中だけで完結する。
 */
export const SIGNALS = {
  moon: {
    label: 'MOON', freq: '88.3 MHz',
    osc: { frequency: 220, type: 'sine', vibrato: { rate: 5, depthCents: 6 } }
  },
  saturn: {
    label: 'SATURN', freq: '104.7 MHz',
    osc: { frequency: 147, type: 'triangle', detuneCents: 7 } // 2本のデチューンでうなり
  },
  emberPlanet: {
    label: 'EMBER PLANET', freq: '66.1 MHz',
    osc: { frequency: 98, type: 'sawtooth', tremolo: { rate: 2.5, depth: 0.3 } }
  },
  auroraMoon: {
    label: 'AURORA MOON', freq: '121.9 MHz',
    osc: { frequency: 523, type: 'sine', vibrato: { rate: 0.8, depthCents: 20 } }
  }
}

const MAX_LEVEL = 0.4 // 照準ぴったり時の音量（SE グループ内での相対値）

export default class SignalAudio {
  constructor(audioManager) {
    this.context = audioManager.mixer.audioContext
    this.output = audioManager.mixer.groups.get('SE').gainNode
    this.channels = null
  }

  start() {
    if (this.channels) return
    if (this.context.state === 'suspended') {
      this.context.resume()
    }

    this.channels = {}
    for (const [name, config] of Object.entries(SIGNALS)) {
      this.channels[name] = this.createChannel(config.osc)
    }
  }

  createChannel(config) {
    const context = this.context
    const nodes = []
    // 音程シフト対象の主オシレーター（LFO は含めない。LFO を動かすとビブラート速度が壊れる）
    const pitchOscs = []

    // 照準の合い具合で開閉するゲート
    const gate = context.createGain()
    gate.gain.value = 0
    gate.connect(this.output)

    const createOsc = (detuneCents = 0) => {
      const osc = context.createOscillator()
      osc.type = config.type
      osc.frequency.value = config.frequency
      osc.detune.value = detuneCents
      nodes.push(osc)
      // 基準デチューン（saturn の2本目のうなり等）を保存し、シフトは相対で掛ける
      pitchOscs.push({ osc, baseDetune: detuneCents })
      return osc
    }

    if (config.tremolo) {
      // 音量をゆっくり波打たせる（0.4〜1.0 で脈動）
      const tremoloNode = context.createGain()
      tremoloNode.gain.value = 1.0 - config.tremolo.depth
      tremoloNode.connect(gate)

      const lfo = context.createOscillator()
      lfo.frequency.value = config.tremolo.rate
      const lfoGain = context.createGain()
      lfoGain.gain.value = config.tremolo.depth
      lfo.connect(lfoGain)
      lfoGain.connect(tremoloNode.gain)
      nodes.push(lfo)

      createOsc().connect(tremoloNode)
    } else {
      const osc = createOsc()
      osc.connect(gate)

      if (config.detuneCents) {
        // 2本目をわずかにデチューンして、ゆっくりしたうなりを作る
        createOsc(config.detuneCents).connect(gate)
      }

      if (config.vibrato) {
        const lfo = context.createOscillator()
        lfo.frequency.value = config.vibrato.rate
        const lfoGain = context.createGain()
        lfoGain.gain.value = config.vibrato.depthCents
        lfo.connect(lfoGain)
        lfoGain.connect(osc.detune)
        nodes.push(lfo)
      }
    }

    for (const node of nodes) {
      node.start()
    }

    return { gate, nodes, pitchOscs }
  }

  setLevel(name, level, detuneCents = 0) {
    const channel = this.channels?.[name]
    if (!channel) return
    const now = this.context.currentTime
    // setTargetAtTime でクリックノイズを避けつつ滑らかに追従
    channel.gate.gain.setTargetAtTime(level * MAX_LEVEL, now, 0.08)
    // 波長シフト: 基準デチューンからの相対セント。ビブラート LFO の変調とは加算合成される
    for (const { osc, baseDetune } of channel.pitchOscs) {
      osc.detune.setTargetAtTime(baseDetune + detuneCents, now, 0.25)
    }
  }

  stop() {
    if (!this.channels) return

    const now = this.context.currentTime
    const channels = Object.values(this.channels)
    this.channels = null

    for (const channel of channels) {
      channel.gate.gain.setTargetAtTime(0, now, 0.08)
    }
    // フェードアウトを待ってからオシレーターを止める
    setTimeout(() => {
      for (const channel of channels) {
        for (const node of channel.nodes) {
          node.stop()
        }
        channel.gate.disconnect()
      }
    }, 400)
  }
}
